// The choice control. A combobox trigger styled like Input with a listbox
// popover: arrows, Home, End, Enter, Escape, first-letter typeahead. options
// is [{ value, label }] and onChange receives the value, not an event.
// Optional label, help, and error render the same shell as Input.

import { useEffect, useId, useRef, useState } from 'react'
import { FieldShell } from '../FieldShell/index.jsx'
import { useOutsideClose } from '../useOutsideClose.js'
import './select.css'

export function Select({
  id,
  value,
  onChange,
  options,
  placeholder = 'Choose',
  label,
  help,
  error,
  ariaLabel,
  disabled = false,
  style,
  className = '',
}) {
  const reactId = useId()
  const baseId = id || reactId
  const wrapRef = useRef(null)
  const triggerRef = useRef(null)
  const listRef = useRef(null)
  const [open, setOpen] = useState(false)
  const selectedIndex = options.findIndex((option) => option.value === value)
  const [activeIndex, setActiveIndex] = useState(Math.max(selectedIndex, 0))

  useOutsideClose(wrapRef, open, () => setOpen(false))

  const selected = selectedIndex >= 0 ? options[selectedIndex] : null

  function openList() {
    setActiveIndex(Math.max(selectedIndex, 0))
    setOpen(true)
  }

  function choose(index) {
    const option = options[index]
    if (!option) return
    onChange(option.value)
    setOpen(false)
    triggerRef.current?.focus()
  }

  useEffect(() => {
    if (!open) return
    listRef.current
      ?.querySelector(`#${CSS.escape(`${baseId}-opt-${activeIndex}`)}`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [open, activeIndex, baseId])

  function onKeyDown(event) {
    if (!open) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault()
        openList()
      }
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      setActiveIndex((index) => Math.min(index + 1, options.length - 1))
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      setActiveIndex((index) => Math.max(index - 1, 0))
    } else if (event.key === 'Home') {
      event.preventDefault()
      setActiveIndex(0)
    } else if (event.key === 'End') {
      event.preventDefault()
      setActiveIndex(options.length - 1)
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault()
      choose(activeIndex)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
    } else if (event.key === 'Tab') {
      setOpen(false)
    } else if (/^[a-z0-9]$/i.test(event.key)) {
      const letter = event.key.toLowerCase()
      const from = activeIndex + 1
      const order = [...options.slice(from), ...options.slice(0, from)]
      const hit = order.findIndex((option) => option.label.toLowerCase().startsWith(letter))
      if (hit >= 0) setActiveIndex((from + hit) % options.length)
    }
  }

  const control = (
    <div
      className={`ui-select${className && !label ? ` ${className}` : ''}`}
      ref={wrapRef}
      style={label ? undefined : style}
    >
      <button
        ref={triggerRef}
        type="button"
        id={baseId}
        className={`ui-select-trigger${selected ? '' : ' is-placeholder'}`}
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={`${baseId}-list`}
        aria-activedescendant={open ? `${baseId}-opt-${activeIndex}` : undefined}
        aria-label={ariaLabel}
        aria-invalid={error ? true : undefined}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openList())}
        onKeyDown={onKeyDown}
      >
        <span className="ui-select-value">{selected ? selected.label : placeholder}</span>
        <span className="ui-select-caret" aria-hidden="true" />
      </button>

      {open ? (
        <div className="ui-popover" role="presentation">
          <ul id={`${baseId}-list`} ref={listRef} className="ui-select-list" role="listbox">
            {options.map((option, index) => (
              <li
                key={String(option.value)}
                id={`${baseId}-opt-${index}`}
                role="option"
                aria-selected={option.value === value}
                className={`ui-select-option${index === activeIndex ? ' is-active' : ''}${
                  option.value === value ? ' is-selected' : ''
                }`}
                onPointerMove={() => setActiveIndex(index)}
                onClick={() => choose(index)}
              >
                {option.label}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  )

  return (
    <FieldShell
      id={baseId}
      label={label}
      help={help}
      error={error}
      className={className}
      style={style}
    >
      {control}
    </FieldShell>
  )
}
